import InfoOutlined from "@mui/icons-material/InfoOutlined";
import MoreVert from "@mui/icons-material/MoreVert";
import {
  Checkbox,
  IconButton,
  Link,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  documentKindNames,
  manifestNumberLabel,
} from "encamp-shared/src/waste/shippingDocumentLabels";
import { ShippingDocumentKind } from "generated-graphql/graphql";
import { useState } from "react";

import { StepAction } from "./StepAction";

/**
 * What a manifest is, for a reader who does not already know whether it applies.
 *
 * It says what the document is and does not say whether THIS shipment needs
 * one: a very small quantity generator may ship hazardous waste without a
 * manifest, and universal waste is only exempt where the generator's state
 * adopts the category (`wasteApplicabilitySchema`, 40 CFR 273.9). A definition
 * phrased as a rule would be wrong in both cases, hence "most hazardous waste".
 */
const MANIFEST_HELP =
  "The EPA Uniform Hazardous Waste Manifest (Form 8700-22) — the federally prescribed form, required for most hazardous waste. Its signed copy comes back from the receiving facility.";

/**
 * The attach action, with an info icon carrying what a manifest is. The icon is beside the button rather than inside it because `StepAction`
 * is shared with the steps above, none of which explain themselves.
 */
function AttachChoice({
  label,
  noun,
  help,
  blocked,
  onClick,
}: {
  label: string;
  /**
   * What the document is called, for the icon's accessible name.
   *
   * Passed rather than derived from `label`: reverse-engineering it by stripping
   * "Attach " turned a copy change into a silently degraded label ("What is a
   * Link manifest?") with nothing failing.
   */
  noun: string;
  help: string;
  blocked: string;
  onClick: () => void;
}) {
  return (
    <Stack direction="row" alignItems="center" spacing={0.5}>
      <StepAction label={label} blocked={blocked} onClick={onClick} />
      {/*
        `describeChild` so the tooltip becomes the icon's DESCRIPTION rather
        than its name. Without it, `Tooltip` puts the help text in an
        `aria-label` on the child, which wins the accessible-name computation
        and leaves the icon announcing a definition with no idea what it is a
        definition of.
      */}
      <Tooltip title={help} describeChild>
        {/*
          `titleAccess` rather than a bare `aria-label`. `SvgIcon` sets
          `aria-hidden` unless it is given one and applies the caller's props
          afterwards, so an `aria-label` alone left a focusable element inside an
          aria-hidden subtree: a screen reader announced neither the label nor
          the tooltip, and these definitions exist for exactly the reader who
          cannot see the popper. `titleAccess` sets `role="img"` and drops
          `aria-hidden`.
        */}
        <InfoOutlined
          tabIndex={0}
          titleAccess={`What is a ${noun}?`}
          sx={{ fontSize: 16, color: "text.secondary", cursor: "help" }}
        />
      </Tooltip>
    </Stack>
  );
}

/**
 * The manifest, under the step that receives it.
 *
 * It used to be a card of its own beside Shipment Details, which repeated what
 * the step already said — the rail has always printed the tracking number and
 * the received date — and left the page carrying the same fact twice. The
 * manifest is a stage of the shipment's life, so it says so where the other
 * stages do.
 *
 * Its actions stay behind one menu rather than becoming a row of icons.
 * The rail is 240–290px wide and deliberately quiet: one control beside the
 * number reads as an aside, where three competing glyphs would make the
 * narrowest column on the page also the busiest.
 *
 * A bill of lading is a manifest row with a different kind, so this renders
 * whichever is attached and names it — `attachedKind` is the resolver's own
 * answer to which one that is.
 */
export function ManifestStepActions({
  attachedKind,
  documentNumber,
  onOpenDocument,
  signed,
  /** True while the link is attached but its record is still rebuilding. */
  dangling,
  attachBlockedReason,
  signedChangePending,
  onAttachManifest,
  onRelink,
  onDetach,
  onSignedChange,
}: {
  /** Null when nothing is attached, which is what selects the empty state. */
  attachedKind: ShippingDocumentKind | null;
  /**
   * The attached document's tracking number. Null while nothing is attached.
   */
  documentNumber: string | null;
  /** Opens the attached document; null while a manifest link dangles. */
  onOpenDocument: (() => void) | null;
  signed: boolean;
  dangling: boolean;
  /** Why attaching is unavailable right now, or undefined when it is not. */
  attachBlockedReason?: string;
  /**
   * True while a signed-copy change is still in flight.
   *
   * Marking a copy signed re-attaches the manifest already linked, so it is the
   * attach mutation under another name — and toggling again before the first
   * lands sends two of them with opposite values, leaving the setting wherever
   * the server happens to finish last.
   */
  signedChangePending?: boolean;
  onAttachManifest: () => void;
  onRelink: () => void;
  onDetach: () => void;
  onSignedChange: (signed: boolean) => void;
}) {
  if (attachedKind == null) {
    return (
      <AttachChoice
        label="Attach manifest"
        noun="manifest"
        help={MANIFEST_HELP}
        blocked={attachBlockedReason ?? ""}
        onClick={onAttachManifest}
      />
    );
  }

  const { noun, sentence } = documentKindNames(attachedKind);
  const isManifest = attachedKind === ShippingDocumentKind.Manifest;
  // The label falls back to what the document is. Left blank, the row rendered
  // an empty link.
  const label = documentNumber
    ? manifestNumberLabel({ documentKind: attachedKind, manifestTrackingNumber: documentNumber })
    : sentence;

  return (
    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
      {/* The number is the only thing here that gives up room, so in a 240px
          rail it is also the only thing that can end in an ellipsis — the
          tooltip is how the whole of it stays readable. */}
      <Tooltip title={label}>
        {onOpenDocument ? (
          // Opens the manifest's drawer over this shipment. The manifest is
          // read AGAINST the shipment, so it has to arrive without costing the
          // reader the page they are comparing it to — which a panel does
          // better than the tab this used to open.
          <Link
            component="button"
            type="button"
            onClick={onOpenDocument}
            variant="body2"
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              minWidth: 0,
              textAlign: "left",
            }}
          >
            <Typography variant="body2" component="span" noWrap sx={{ minWidth: 0 }}>
              {label}
            </Typography>
          </Link>
        ) : (
          <Typography variant="body2" noWrap sx={{ minWidth: 0 }}>
            {label}
          </Typography>
        )}
      </Tooltip>
      <ManifestActionsMenu
        noun={noun}
        // Standalone labels need the sentence-cased form; the menu items are
        // mid-sentence and take the plain noun.
        title={sentence}
        // Signing records that the signed copy came back. A bill of lading has
        // no signed return leg, so the item is absent rather than present and
        // inert.
        showSigned={isManifest}
        signed={signed}
        // Nothing to act on while the record is rebuilding: all three act on a
        // manifest row that is not there yet.
        disabled={dangling}
        disabledReason={
          dangling ? `Available once the ${noun} record has finished rebuilding.` : undefined
        }
        // Replacing attaches, so it locks the contents the same way a first
        // attach does — and is blocked for the same reason.
        replaceBlockedReason={attachBlockedReason}
        signedChangePending={signedChangePending}
        onSignedChange={onSignedChange}
        onRelink={onRelink}
        onDetach={onDetach}
      />
    </Stack>
  );
}

/**
 * The attached document's actions, behind one control.
 *
 * Signing is a checkbox item rather than a switch because it sits in the same
 * list as the ones that are not toggles, and a menu of mixed shapes reads worse
 * than one that states its own state. It is also the only one that is
 * reversible, which a checkbox says and a plain item would not.
 */
function ManifestActionsMenu({
  noun,
  title,
  showSigned,
  signed,
  disabled,
  disabledReason,
  replaceBlockedReason,
  signedChangePending,
  onSignedChange,
  onRelink,
  onDetach,
}: {
  /** What the attached document is called, mid-sentence, for the menu items. */
  noun: string;
  /** The same noun sentence-cased, for the labels that stand on their own. */
  title: string;
  /** Whether the signed-copy item applies at all; see the caller. */
  showSigned: boolean;
  signed: boolean;
  disabled: boolean;
  /** Why the actions are unavailable, when they are. */
  disabledReason?: string;
  /** Why replacing specifically is unavailable — it attaches, so it locks. */
  replaceBlockedReason?: string;
  /** True while a signed-copy change is still in flight; see the caller. */
  signedChangePending?: boolean;
  onSignedChange: (signed: boolean) => void;
  onRelink: () => void;
  onDetach: () => void;
}) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const close = () => setAnchor(null);
  const run = (action: () => void) => () => {
    close();
    action();
  };

  return (
    <>
      <Tooltip title={disabledReason ?? `${title} actions`}>
        <span>
          <IconButton
            size="small"
            aria-label={`${title} actions`}
            disabled={disabled}
            onClick={(e) => setAnchor(e.currentTarget)}
          >
            <MoreVert fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={close}>
        {/*
          Held while its own change is still in flight. This item re-attaches the
          manifest to record the new value, so toggling it again before the first
          lands puts two of the same mutation in the air with opposite values and
          the setting ends up wherever the server finishes last. Nothing explains
          the pause because it lasts as long as the request does.
        */}
        {showSigned && (
          <MenuItem disabled={signedChangePending} onClick={run(() => onSignedChange(!signed))}>
            <ListItemIcon>
              <Checkbox size="small" edge="start" checked={signed} tabIndex={-1} disableRipple />
            </ListItemIcon>
            <ListItemText primary="Mark as signed copy" />
          </MenuItem>
        )}
        {/*
          Wrapped only while it is unavailable, which is the one state where the
          wrapper earns its keep.

          A span around a disabled control is the standard way to make a tooltip
          work at all, since a disabled child drops the pointer events the tooltip
          listens for. But `MenuList` moves focus across its own direct children,
          so a permanent wrapper left this item unreachable by arrow keys even
          when it was enabled and was the thing the user came for. Disabled items
          are skipped anyway, so wrapping only then costs nothing.

          The reason belongs in the tooltip rather than in the item. Inside it, as
          the label it displaced what the item does, and as secondary text it
          inherited the item's disabled opacity on top of its own — landing near
          1.6:1 against the menu, which is not readable. Either way `MenuItem` does
          not wrap text, so a sentence-long reason stretched the popover.
        */}
        {replaceBlockedReason ? (
          <Tooltip title={replaceBlockedReason}>
            <span>
              <MenuItem disabled>
                <ListItemText primary={`Replace this ${noun}`} />
              </MenuItem>
            </span>
          </Tooltip>
        ) : (
          <MenuItem onClick={run(onRelink)}>
            <ListItemText primary={`Replace this ${noun}`} />
          </MenuItem>
        )}
        <MenuItem onClick={run(onDetach)}>{`Detach this ${noun}`}</MenuItem>
      </Menu>
    </>
  );
}
